import { useTranslation } from 'react-i18next';
import useBanks from '@/hooks/useBanks';
import LabeledInput, { SearchableSelect } from '@/pages/newSupported/components/LabeledInput';
import { PaymentMethodType } from '@/models/common/PaymentMethodType';
import addIcon from '@/assets/icons/add.svg';
import minusIcon from '@/assets/icons/minus.svg';

export interface PaymentInfoFormData {
  accountOwnerName: string;
  bankNumber: number;
  branchNumber: string;
  accountNumber: string;
  type: PaymentMethodType;
}

interface AddPaymentInfoFormProps {
  paymentInfos: PaymentInfoFormData[];
  onPaymentInfosChange: (paymentInfos: PaymentInfoFormData[]) => void;
  /**
   * Exactly one account, where the caller supplies the heading: the fields only, without the title
   * and the "add another account" button. Used to replace a returned account (HAT-1395).
   */
  single?: boolean;
}

export const createEmptyPaymentInfo = (): PaymentInfoFormData => ({
  accountOwnerName: '',
  bankNumber: 0,
  branchNumber: '',
  accountNumber: '',
  type: PaymentMethodType.BankAccount,
});

const AddPaymentInfoForm = ({ paymentInfos, onPaymentInfosChange, single = false }: AddPaymentInfoFormProps) => {
  const { t } = useTranslation();

  const handleAddPaymentInfo = () => {
    onPaymentInfosChange([...paymentInfos, createEmptyPaymentInfo()]);
  };

  const handleRemovePaymentInfo = (index: number) => {
    onPaymentInfosChange(paymentInfos.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index: number, field: keyof PaymentInfoFormData, value: string | number) => {
    const updated = [...paymentInfos];
    updated[index] = { ...updated[index], [field]: value };
    onPaymentInfosChange(updated);
  };

  const renderPaymentInfoFields = (paymentInfo: PaymentInfoFormData, index: number) => (
    <div key={index} className="flex flex-col gap-4">
      {index > 0 ? (
        <div className="bg-(--surface-grey-lowest) border border-(--border-lowest) rounded-2xl p-8 max-[1099px]:p-4 relative shadow-sm">
          <div className="flex flex-col gap-6">
            {/* Row 1: Beneficiary Name (Account Owner) */}
            <div className="flex gap-8">
              <LabeledInput
                label={t('beneficiaryName')}
                id={`accountOwnerName-${index}`}
                width="414px"
                value={paymentInfo.accountOwnerName}
                onChange={(e) => handleFieldChange(index, 'accountOwnerName', e.target.value)}
                placeholder={t('beneficiaryNamePlaceholder')}
                required
                mobileFullWidth
              />
            </div>

            {/* Row 2: Bank Code, Branch Number, Account Number */}
            <div className="flex gap-8 max-[1099px]:flex-col max-[1099px]:gap-4">
              <SearchableSelect
                label={t('bankCode')}
                id={`bankNumber-${index}`}
                width="262.67px"
                onChange={(value) => {
                  const bankNum = parseInt(value.split(' - ')[0]);
                  handleFieldChange(index, 'bankNumber', isNaN(bankNum) ? 0 : bankNum);
                }}
                loadOptions={useBanks.getBankOptions}
                placeholder="bankCodePlaceholder"
                required
                mobileFullWidth
              />
              <SearchableSelect
                label={t('branchNumber')}
                id={`branchNumber-${index}`}
                width="262.67px"
                onChange={(value) => {
                  const branchNum = value.split(' - ')[0];
                  handleFieldChange(index, 'branchNumber', branchNum);
                }}
                loadOptions={useBanks.getBranchOptions}
                dependency={paymentInfo.bankNumber ? `${paymentInfo.bankNumber} - ` : ''}
                placeholder="branchNumberPlaceholder"
                dependencyPlaceholder="selectBankFirst"
                required
                mobileFullWidth
              />
              <LabeledInput
                label={t('accountNumber')}
                id={`accountNumber-${index}`}
                width="262.67px"
                value={paymentInfo.accountNumber}
                onChange={(e) => handleFieldChange(index, 'accountNumber', e.target.value.replace(/\D/g, ''))}
                placeholder={t('accountNumberPlaceholder')}
                required
                mobileFullWidth
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleRemovePaymentInfo(index)}
            className="absolute start-[-12px] top-6 w-6 h-6 bg-(--font-error) rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
            title={t('removeBankAccount')}
          >
            <img src={minusIcon} alt="" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {/* Row 1: Beneficiary Name (Account Owner) */}
          <div className="flex gap-8">
            <LabeledInput
              label={t('beneficiaryName')}
              id={`accountOwnerName-${index}`}
              width="414px"
              value={paymentInfo.accountOwnerName}
              onChange={(e) => handleFieldChange(index, 'accountOwnerName', e.target.value)}
              placeholder={t('beneficiaryNamePlaceholder')}
              required
              mobileFullWidth
            />
          </div>

          {/* Row 2: Bank Code, Branch Number, Account Number */}
          <div className="flex gap-8 max-[1099px]:flex-col max-[1099px]:gap-4">
            <SearchableSelect
              label={t('bankCode')}
              id={`bankNumber-${index}`}
              width="262.67px"
              onChange={(value) => {
                const bankNum = parseInt(value.split(' - ')[0]);
                handleFieldChange(index, 'bankNumber', isNaN(bankNum) ? 0 : bankNum);
              }}
              loadOptions={useBanks.getBankOptions}
              placeholder="bankCodePlaceholder"
              required
              mobileFullWidth
            />
            <SearchableSelect
              label={t('branchNumber')}
              id={`branchNumber-${index}`}
              width="262.67px"
              onChange={(value) => {
                const branchNum = value.split(' - ')[0];
                handleFieldChange(index, 'branchNumber', branchNum);
              }}
              loadOptions={useBanks.getBranchOptions}
              dependency={paymentInfo.bankNumber ? `${paymentInfo.bankNumber} - ` : ''}
              placeholder="branchNumberPlaceholder"
              dependencyPlaceholder="selectBankFirst"
              required
              mobileFullWidth
            />
            <LabeledInput
              label={t('accountNumber')}
              id={`accountNumber-${index}`}
              width="262.67px"
              value={paymentInfo.accountNumber}
              onChange={(e) => handleFieldChange(index, 'accountNumber', e.target.value.replace(/\D/g, ''))}
              placeholder={t('accountNumberPlaceholder')}
              required
              mobileFullWidth
            />
          </div>
        </div>
      )}
    </div>
  );

  if (single) {
    return renderPaymentInfoFields(paymentInfos[0] ?? createEmptyPaymentInfo(), 0);
  }

  if (paymentInfos.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('addPaymentInfo')}</h4>
        <button
          type="button"
          onClick={handleAddPaymentInfo}
          className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
        >
          <div className="w-[10px] h-[10px] relative">
            <img src={addIcon} alt="" />
          </div>
          {t('addBankAccount')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <h4 className="body-lr-semibold text-(--font-sub-title-2)">{t('addPaymentInfo')}</h4>

      {paymentInfos.map((paymentInfo, index) => renderPaymentInfoFields(paymentInfo, index))}

      <button
        type="button"
        onClick={handleAddPaymentInfo}
        className="bg-(--surface-base) border border-(--border-main) rounded-lg h-9 px-3 flex items-center justify-center gap-1 body-sm-semibold text-(--font-primary) self-start"
      >
        <div className="w-[10px] h-[10px] relative">
          <img src={addIcon} alt="" />
        </div>
        {t('addAnotherBankAccount')}
      </button>
    </div>
  );
};

export default AddPaymentInfoForm;
